import { http, pagination, id } from 'utils'
import { concat, merge, set } from 'lodash'
import { SET_USERS, SET_META, ADD_USERS, SET_USER, RESET_USER, SET_PROPERTY } from 'vx/mutations'

export const initialUserState = () => ({
  first_name: null,
  last_name: null,
  username: null,
  email: null,
  role: null,
  agency_id: null,
  region: null,
  phone: null,
  unenrolled: false,
  gen: null,
  enabled: true
})

// Users state.
const state = {
  user: initialUserState(),
  users: [],
  meta: null
}

// Users state getters.
const getters = {}

// Users actions
const actions = {
  /**
   * Get all users.
   *
   * @return Promise
   */
  async getUsers ({ commit }, params = {}) {
    commit(SET_USERS, [])
    commit(SET_META, null)

    const response = await http.get('users', { params })

    commit(SET_USERS, response.data.data)
    commit(SET_META, response.data.meta)

    return response
  },

  /**
   * Get next lot of users
   *
   * @param {*} params
   */
  async getUsersForNextPage ({ commit, state }, params = {}) {
    if (pagination(state.meta)) {
      return Promise.reject(new Error('No more results.'))
    }

    params.page = state.meta.current_page + 1

    const response = await http.get('users', { params })

    commit(ADD_USERS, response.data.data)
    commit(SET_META, response.data.meta)
  },

  /**
   * Get a user by id
   *
   * @param integer id
   */
  async getUser ({ commit }, id) {
    commit(RESET_USER)

    const response = await http.get(`users/${id}`)

    commit(SET_USER, response.data.data)

    return response
  },

  /**
   * Create new user and set on state
   *
   * @param object payload
   */
  async storeUser ({ commit }, payload) {
    const response = await http.post(`users`, payload)

    commit(SET_USER, response.data.data)

    return response
  },

  /**
   * Update an existing user
   *
   * @param object|int user
   * @param oject payload
   */
  updateUser ({ state }, { user, payload }) {
    return http.put(`users/${id(user)}`, payload)
  },

  /**
   * Enable an existing user
   *
   * @param object|int user
   * @param oject payload
   */
  async enableUser ({ state, dispatch }) {
    const response = await http.put(`users/${id(state.user)}/status`, { enabled: true })

    dispatch('setUserProperty', { property: 'enabled', value: true })

    return response
  },

  /**
   * Disable an existing user
   *
   * @param object|int user
   * @param oject payload
   */
  async disableUser ({ state, dispatch }) {
    const response = await http.put(`users/${id(state.user)}/status`, { enabled: false })

    dispatch('setUserProperty', { property: 'enabled', value: false })

    return response
  },

  /**
   * Generate a one time password for a user
   *
   * @param {*} user
   */
  async storeOneTimePassword ({ commit }, user) {
    const response = await http.post(`users/${id(user)}/one-time-password`)

    return response.data.data.password
  },

  /**
   * Update an existing user's password
   *
   * @param object|int user
   * @param oject payload
   */
  updatePassword ({ state }, payload) {
    return http.put(`user/password`, payload)
  },

  /**
   * Update the logged in users account
   *
   * @param {*} user
   */
  updateAccount ({ commit }, payload) {
    return http.put(`user`, payload)
  },

  /**
   * Reset user to initial state
   */
  resetUser ({ commit }) {
    commit(RESET_USER)
  },

  /**
   * Set a property on a user
   *
   * @param object payload
   */
  setUserProperty ({ commit }, payload) {
    commit(SET_PROPERTY, payload)
  },

  async sendPasswordResetEmail (store, payload) {
    const response = await http.post('user/send-password-reset-email', payload)
    return response
  }
}

// Users state mutations.
const mutations = {
  [SET_USERS] (state, users) {
    state.users = users
  },

  [SET_META] (state, meta) {
    state.meta = meta
  },

  [ADD_USERS] (state, users) {
    state.users = concat(state.users, users)
  },

  [SET_USER] (state, user) {
    state.user = merge(initialUserState(), user)
  },

  [RESET_USER] (state, user) {
    state.user = initialUserState()
  },

  [SET_PROPERTY] (state, { property, value }) {
    set(state.user, property, value)
  }
}

// Export our state, getters, actions, and mutations.
export default {
  namespaced: true, state, getters, actions, mutations
}
